/**
 * AdSourceWidget — T6 «Ad revenue by traffic source and campaign (ads-only ROAS)»
 * ====================================================================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, задача T6.
 * КОГОРТНЫЙ дашборд: глобальный диапазон дат дашборда трактуется сервером как
 * когорта регистрации (`reg_date`), а не дата события — как в ретеншене/
 * прогрессии/движении ресурсов. Грани adset/ad исключены по спеке (~62% fill).
 *
 * Три вьюхи:
 * 1) «По источникам» — сортируемая таблица (cost, installs, ad LTV_d, ads-only
 *    ROAS_d, доля рекламы) + бар доли рекламы в ROAS по источникам (сортировано,
 *    одна тональность) + stacked bar рекламной выручки по источникам по неделям
 *    (категориальная палитра — part-to-whole).
 * 2) «По кампаниям» — drill-down внутри выбранного источника (топ N кампаний).
 *
 * Пустой/`restricted` source сервер возвращает как «Unattributed» — показываем
 * как обычную строку; сумма по строкам сходится к тоталу prod_admon за когортное
 * окно (см. verify-ads-sources.ts).
 */

import React, { useState, useMemo } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '../../ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Cell } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { adsSources as adsSourcesSpecs } from '@ggstat/chart-specs';

const MATURITY_DAYS = adsSourcesSpecs.sources.maturityDays;
const CAMPAIGN_LIMIT = adsSourcesSpecs.sources.campaignLimit;

const SOURCE_COLORS = adsSourcesSpecs.sources.palette;
const AD_SHARE_STROKE = adsSourcesSpecs.sourcesAdShare.series[0].color;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v, digits = 1) => `${(Number(v || 0) * 100).toFixed(digits)}%`;

function heatColor(value, max) {
  if (!max || value <= 0) return 'rgba(59, 130, 246, 0.04)';
  const t = Math.min(1, value / max);
  const alpha = 0.08 + t * 0.82;
  return `rgba(59, 130, 246, ${alpha.toFixed(3)})`;
}

/** Заголовок таблицы с сортировкой по клику (как в TopIapWidget). */
function SortableHead({ label, sortKey, sortConfig, onSort, className = '' }) {
  return (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {label} {sortConfig.key === sortKey && (sortConfig.direction === 'asc' ? '↑' : '↓')}
    </TableHead>
  );
}

/** Схлопывает по-периодные строки в одну строку на измерение (source/campaign) для таблицы за всё окно. */
function collapseByDimension(rows, dimKey) {
  const map = new Map();
  for (const row of rows) {
    const key = row[dimKey];
    if (!map.has(key)) {
      map.set(key, {
        [dimKey]: key,
        adRevenue: 0,
        adRevenueMaturity: 0,
        impressions: 0,
        cost: 0,
        installs: 0,
        iapRevenueMaturity: 0
      });
    }
    const acc = map.get(key);
    acc.adRevenue += row.adRevenue;
    acc.adRevenueMaturity += row.adRevenueMaturity;
    acc.impressions += row.impressions;
    acc.cost += row.cost;
    acc.installs += row.installs;
    acc.iapRevenueMaturity += row.iapRevenueMaturity;
  }
  return [...map.values()].map((acc) => ({
    ...acc,
    adLtvD: acc.installs > 0 ? acc.adRevenueMaturity / acc.installs : 0,
    adsOnlyRoasD: acc.cost > 0 ? acc.adRevenueMaturity / acc.cost : 0,
    adShareOfRoasD:
      acc.adRevenueMaturity + acc.iapRevenueMaturity > 0
        ? acc.adRevenueMaturity / (acc.adRevenueMaturity + acc.iapRevenueMaturity)
        : 0
  }));
}

/** Таблица по источникам/кампаниям за всё окно, сортируемая. */
function DimensionTable({ rows, dimKey, dimLabel, maturityDay }) {
  const [sortConfig, setSortConfig] = useState({ key: 'adRevenue', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      const diff = typeof av === 'string' ? String(av).localeCompare(String(bv)) : Number(av) - Number(bv);
      return sortConfig.direction === 'asc' ? diff : -diff;
    });
    return copy;
  }, [rows, sortConfig]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          adRevenue: acc.adRevenue + r.adRevenue,
          cost: acc.cost + r.cost,
          installs: acc.installs + r.installs
        }),
        { adRevenue: 0, cost: 0, installs: 0 }
      ),
    [rows]
  );

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead label={dimLabel} sortKey={dimKey} sortConfig={sortConfig} onSort={handleSort} />
            <SortableHead label="Cost" sortKey="cost" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
            <SortableHead label="Installs" sortKey="installs" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
            <SortableHead
              label={`Ad LTV_d${maturityDay}`}
              sortKey="adLtvD"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <SortableHead
              label={`Ads-only ROAS_d${maturityDay}`}
              sortKey="adsOnlyRoasD"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <SortableHead
              label="Доля рекламы в ROAS"
              sortKey="adShareOfRoasD"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-right"
            />
            <SortableHead label="Ad revenue" sortKey="adRevenue" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row[dimKey]}>
              <TableCell className="font-medium">{row[dimKey]}</TableCell>
              <TableCell className="text-right">{fmtMoney(row.cost)}</TableCell>
              <TableCell className="text-right">{fmtInt(row.installs)}</TableCell>
              <TableCell className="text-right">{fmtMoney(row.adLtvD)}</TableCell>
              <TableCell className="text-right font-medium">{fmtPct(row.adsOnlyRoasD)}</TableCell>
              <TableCell className="text-right">{fmtPct(row.adShareOfRoasD)}</TableCell>
              <TableCell className="text-right">{fmtMoney(row.adRevenue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell>Итого</TableCell>
            <TableCell className="text-right">{fmtMoney(totals.cost)}</TableCell>
            <TableCell className="text-right">{fmtInt(totals.installs)}</TableCell>
            <TableCell colSpan={2} className="text-right text-muted-foreground text-xs">
              LTV/ROAS не суммируются построчно
            </TableCell>
            <TableCell />
            <TableCell className="text-right font-medium">{fmtMoney(totals.adRevenue)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

function ShareTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">{row.source}</p>
      <p>Доля рекламы в ROAS: {fmtPct(row.adShareOfRoasD)}</p>
      <p>Ad revenue: {fmtMoney(row.adRevenue)}</p>
    </div>
  );
}

/** Бар доли рекламы в ROAS по источникам — сортировано, одна тональность (magnitude). */
function AdShareBar({ rows }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => b.adShareOfRoasD - a.adShareOfRoasD), [rows]);
  const maxShare = sorted.length > 0 ? sorted[0].adShareOfRoasD : 0;

  return (
    <ResponsiveContainer width="100%" height={Math.max(220, sorted.length * 32)}>
      <BarChart data={sorted} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
        <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fill: 'currentColor' }} className="text-xs" />
        <YAxis type="category" dataKey="source" width={130} tick={{ fill: 'currentColor' }} className="text-xs" />
        <Tooltip content={<ShareTooltip />} />
        <Bar dataKey="adShareOfRoasD" isAnimationActive={false} radius={[0, 3, 3, 0]}>
          {sorted.map((row) => (
            <Cell key={row.source} fill={heatColor(row.adShareOfRoasD, maxShare)} stroke={AD_SHARE_STROKE} strokeWidth={1} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Stacked bar рекламной выручки по источникам за период (категориальная палитра, part-to-whole). */
function RevenueByWeekChart({ rows }) {
  const { sources, data } = useMemo(() => {
    const periodsSet = new Set();
    const sourcesSet = new Set();
    rows.forEach((r) => {
      periodsSet.add(r.period);
      sourcesSet.add(r.source);
    });
    const periodsList = [...periodsSet].sort();
    const sourcesList = [...sourcesSet];

    const dataList = periodsList.map((period) => {
      const row = { period };
      rows.filter((r) => r.period === period).forEach((r) => {
        row[r.source] = r.adRevenue;
      });
      return row;
    });

    return { sources: sourcesList, data: dataList };
  }, [rows]);

  if (data.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">Нет данных за период</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={360}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey="period" tick={{ fill: 'currentColor' }} className="text-xs" />
        <YAxis tick={{ fill: 'currentColor' }} className="text-xs" tickFormatter={(v) => `$${fmtInt(v)}`} />
        <Tooltip order="stack" formatter={(value, name) => [fmtMoney(value), name]} />
        <Legend />
        {sources.map((source, i) => (
          <Bar
            key={source}
            dataKey={source}
            stackId="revenue"
            fill={SOURCE_COLORS[i % SOURCE_COLORS.length]}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Вьюха «По источникам». */
function BySourceView({ bySource, maturityDay }) {
  const totalRows = useMemo(() => collapseByDimension(bySource, 'source'), [bySource]);

  return (
    <div className="space-y-6">
      <DimensionTable rows={totalRows} dimKey="source" dimLabel="Источник" maturityDay={maturityDay} />
      <div>
        <p className="text-sm font-medium mb-2">Доля рекламы в ROAS по источникам</p>
        <AdShareBar rows={totalRows} />
      </div>
      <div>
        <p className="text-sm font-medium mb-2">Рекламная выручка по источникам по периодам</p>
        <RevenueByWeekChart rows={bySource} />
      </div>
    </div>
  );
}

/** Вьюха «По кампаниям»: drill-down внутри выбранного источника. */
function ByCampaignView({ maturityDay, groupBy, sourceOptions, selectedSource, onSelectSource }) {
  const campaignData = useAdsWidgetData(
    async (filters, options) =>
      selectedSource
        ? AdsService.get(
            '/sources',
            filters,
            { groupBy, maturityDay, dimension: 'campaign', campaignSource: selectedSource, campaignLimit: CAMPAIGN_LIMIT },
            options
          )
        : Promise.resolve(null),
    [groupBy, maturityDay, selectedSource]
  );

  const totalRows = useMemo(
    () => collapseByDimension(campaignData.data?.campaigns || [], 'campaign'),
    [campaignData.data]
  );

  return (
    <div className="space-y-4">
      <Select value={selectedSource || undefined} onValueChange={onSelectSource}>
        <SelectTrigger className="w-full sm:w-[320px] h-9 text-xs">
          <SelectValue placeholder="Выберите источник" />
        </SelectTrigger>
        <SelectContent>
          {sourceOptions.map((s) => (
            <SelectItem key={s} value={s}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {campaignData.loading && <div className="text-sm text-muted-foreground py-4">Загрузка кампаний…</div>}
      {!campaignData.loading && totalRows.length === 0 && (
        <div className="text-sm text-muted-foreground py-4">Нет кампаний для этого источника за период</div>
      )}
      {!campaignData.loading && totalRows.length > 0 && (
        <DimensionTable rows={totalRows} dimKey="campaign" dimLabel="Кампания" maturityDay={maturityDay} />
      )}
      <p className="text-xs text-muted-foreground">
        Топ {CAMPAIGN_LIMIT} кампаний по выручке. Грани adset/ad исключены по спеке (~62% заполнения).
      </p>
    </div>
  );
}

export function AdSourceWidget() {
  const [view, setView] = useState('source');
  const [groupBy, setGroupBy] = useState('week');
  const [maturityDay, setMaturityDay] = useState(30);
  const [selectedSource, setSelectedSource] = useState(null);

  const sourceData = useAdsWidgetData(
    async (filters, options) => AdsService.get('/sources', filters, { groupBy, maturityDay }, options),
    [groupBy, maturityDay]
  );

  const widgetProps = useBaseWidget(sourceData);
  const { data } = widgetProps;
  const bySource = data?.bySource || [];

  const sourceOptions = useMemo(
    () => [...new Set((data?.bySource || []).map((r) => r.source))].sort(),
    [data]
  );

  const Controls = () => (
    <div className="flex flex-wrap items-center gap-3">
      <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)}>
        <ToggleGroupItem value="source" aria-label="По источникам">По источникам</ToggleGroupItem>
        <ToggleGroupItem value="campaign" aria-label="По кампаниям">По кампаниям</ToggleGroupItem>
      </ToggleGroup>
      <ToggleGroup type="single" value={groupBy} onValueChange={(v) => v && setGroupBy(v)}>
        <ToggleGroupItem value="week" aria-label="Неделя">Неделя</ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Месяц">Месяц</ToggleGroupItem>
      </ToggleGroup>
      <Select value={String(maturityDay)} onValueChange={(v) => setMaturityDay(Number(v))}>
        <SelectTrigger className="w-[110px] h-9 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MATURITY_DAYS.map((d) => (
            <SelectItem key={d} value={String(d)}>
              d{d}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const footnote = data?.footnote?.unknownCohortRevenue;
  const subtitleParts = [];
  if (data?.window) subtitleParts.push(`Когорта (reg_date): ${data.window.from} — ${data.window.to}`);
  if (footnote != null && footnote > 0) {
    subtitleParts.push(`сноска: выручка неизвестной когорты (reg_date=1970-01-01) — ${fmtMoney(footnote)}, исключена`);
  }

  return (
    <BaseWidget
      {...widgetProps}
      title="Реклама по источникам и кампаниям (ads-only ROAS)"
      subtitle={subtitleParts.join(' · ')}
      headerAction={<Controls />}
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={8}
    >
      {view === 'source' ? (
        <BySourceView bySource={bySource} maturityDay={maturityDay} />
      ) : (
        <ByCampaignView
          maturityDay={maturityDay}
          groupBy={groupBy}
          sourceOptions={sourceOptions}
          selectedSource={selectedSource || sourceOptions[0] || null}
          onSelectSource={setSelectedSource}
        />
      )}
    </BaseWidget>
  );
}

export default AdSourceWidget;
